import { z } from 'zod'
import type { Answer } from './answer.js'
import { FieldId } from './id.js'
import type { Side } from './identity.js'

/**
 * Wie een adviseur of beoordelaar ziet voordat hij zijn belangenverklaring geeft (besluit van de
 * gebruiker van 30 oktober 2026, R-184d, R-184g, R-184h, ontwerp 7.4a).
 *
 * Wie verklaart dat hij geen belang heeft, moet weten bij wie dat belang zou liggen: de titel van de
 * aanvraag, de aanvrager en de mede-aanvragers, met hun organisatie als die er is. Meer niet: de
 * inhoud van het formulier ziet hij pas na "geen belang".
 *
 * **De titel en de aanvrager komen standaard uit het dossier** (besluit van de gebruiker van 31 oktober
 * 2026, R-184i). Het processysteem kent de aanvrager, en geeft hem bij het uitzetten mee in het
 * dossier-contract, in `aanvrager` en `aanvragerOrganisatie`, naast de `titel` die er al stond
 * (`BELANG_DOSSIERVELDEN`, `docs/koppelvlak.md` hoofdstuk 4). Een formulier dat de verklaring gebruikt
 * (advies staat aan, of er is een instelling) leest ze daar zonder dat er een formulierveld voor nodig
 * is; de opslagdienst neemt ze dan aan (`belangDossierveldenOf`, `apps/store`, `dossierFields`).
 *
 * **Een koppeling naar formuliervelden gaat voor, per onderdeel.** Wijst de inrichter de titel of de
 * aanvrager aan in het formulier, dan komt dat onderdeel uit die velden en niet uit het dossier; een
 * veld dat een veld van het processysteem toont (`dossierveld`, R-54, R-56) brengt dan zijn gegeven uit
 * het dossier mee. De mede-aanvragers komen altijd uit een herhaalgroep in het formulier: het
 * dossier-contract kent ze niet.
 *
 * **Geen gegeven is geen gok.** Gebruikt een formulier de verklaring niet, dan noemt ze geen partijen;
 * is een aangewezen veld of een waarde in het dossier leeg, dan staat het er niet. Er wordt nooit iets
 * verzonnen of uit een ander veld geraden.
 *
 * Een eigen instelling op de definitie en niet een deel van `AdviesSetting`: de verklaring is een
 * herbruikbare control (besluit van 30 oktober 2026), en een beoordelingsformulier of het bouwblok
 * adviesrapport kan dezelfde partijen noemen zonder dat er advies aan staat.
 */

/** De controltypes die een naam, organisatie of titel kunnen dragen: één regel of meer regels platte tekst. */
export const BELANG_CONTROLS = ['text', 'textarea'] as const

/**
 * Eén partij uit twee losse velden: een veld met de naam en, als het er is, een veld met de
 * organisatie. Zo staat de aanvrager meestal in een formulier, of als alleen-lezen velden uit het
 * dossier.
 */
export const BelangPartijVelden = z.strictObject({
  naam: FieldId,
  organisatie: FieldId.optional(),
})
export type BelangPartijVelden = z.infer<typeof BelangPartijVelden>

/**
 * Meerdere partijen uit een herhaalgroep: per rij één partij, met de kolom die de naam is en, als
 * die er is, de kolom die de organisatie is. Zo vult een aanvrager zijn mede-aanvragers in. Andere
 * kolommen van de groep (een e-mailadres, een rol) gaan niet mee.
 */
export const BelangPartijGroep = z.strictObject({
  groep: FieldId,
  naam: FieldId,
  organisatie: FieldId.optional(),
})
export type BelangPartijGroep = z.infer<typeof BelangPartijGroep>

/**
 * De instelling in de definitie: welke velden de titel, de aanvrager en de mede-aanvragers noemen.
 * Alles optioneel; wat niet is aangewezen, staat niet in de verklaring. Het bevriest met de
 * publicatie (D-36), zoals elke instelling van het formulier.
 */
export const BelangenverklaringSetting = z.strictObject({
  titel: FieldId.optional(),
  aanvrager: BelangPartijVelden.optional(),
  medeAanvragers: BelangPartijGroep.optional(),
})
export type BelangenverklaringSetting = z.infer<typeof BelangenverklaringSetting>

/** Eén partij zoals de verklaring haar toont. Geen persoonssleutel, geen rij-id: alleen wat er staat. */
export const BelangPartij = z.strictObject({
  naam: z.string().min(1),
  organisatie: z.string().min(1).nullable(),
})
export type BelangPartij = z.infer<typeof BelangPartij>

/**
 * Wat de pod aan de adviseur geeft vóór zijn verklaring (`GET /advies`, per verzoek `partijen`), en
 * wat de control `Belangenverklaring` van de renderer toont.
 */
export const BelangPartijen = z.strictObject({
  titel: z.string().min(1).nullable(),
  aanvrager: BelangPartij.nullable(),
  medeAanvragers: z.array(BelangPartij),
})
export type BelangPartijen = z.infer<typeof BelangPartijen>

/** Wat de afleiding van een veld moet weten: zijn id en of het ook voor externen zichtbaar is (R-170). */
interface FieldFacts {
  readonly id: string
  readonly externZichtbaar?: boolean | undefined
}

/**
 * De namen in het dossier-contract waaruit de verklaring de titel en de aanvrager leest (R-184i,
 * `docs/koppelvlak.md` hoofdstuk 4). Ze liggen vast: het koppelvlak noemt ze, en het processysteem levert
 * ze onder die naam.
 */
export const BELANG_DOSSIERVELDEN = { titel: 'titel', aanvrager: 'aanvrager', organisatie: 'aanvragerOrganisatie' } as const

/** Het deel van de definitie dat de afleiding leest. */
export interface BelangSource {
  readonly advies?: { readonly aan: boolean } | undefined
  readonly belangenverklaring?: BelangenverklaringSetting | undefined
  readonly secties: readonly {
    readonly velden: readonly (FieldFacts & { readonly rijVelden?: readonly FieldFacts[] })[]
  }[]
}

/** De platte tekst van een antwoord, of null als er niets staat. */
function textOf(answer: Answer | undefined): string | null {
  if (answer === undefined || !('waarde' in answer)) return null
  const value = answer.waarde
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/**
 * Of dit formulier de belangenverklaring gebruikt: advies staat aan, of de inrichter koppelde partijen.
 * Alleen dan leest de verklaring het dossier, en alleen dan neemt de opslagdienst de velden daarvoor aan.
 */
export function usesBelangenverklaring(definitie: Pick<BelangSource, 'advies' | 'belangenverklaring'>): boolean {
  return definitie.advies?.aan === true || definitie.belangenverklaring !== undefined
}

/**
 * De velden van het dossier-contract die de verklaring van dit formulier leest: de titel en de aanvrager
 * met zijn organisatie, behalve een onderdeel dat de inrichter aan formuliervelden koppelde. Leeg als
 * het formulier de verklaring niet gebruikt. De opslagdienst neemt precies deze aan zonder formulierveld.
 */
export function belangDossierveldenOf(definitie: Pick<BelangSource, 'advies' | 'belangenverklaring'>): readonly string[] {
  if (!usesBelangenverklaring(definitie)) return []
  const setting = definitie.belangenverklaring
  return [
    ...(setting?.titel === undefined ? [BELANG_DOSSIERVELDEN.titel] : []),
    ...(setting?.aanvrager === undefined ? [BELANG_DOSSIERVELDEN.aanvrager, BELANG_DOSSIERVELDEN.organisatie] : []),
  ]
}

/** De platte tekst van een waarde uit het dossier, of null als het geen tekst is of leeg. */
function dossierText(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed === '' ? null : trimmed
}

/**
 * De partijen van deze instantie, zoals de definitie ze aanwijst, uit de stand van de instantie en uit
 * wat het processysteem in het dossier-contract meegaf (`dossier`, de velden van `DossierFields`).
 *
 * Null als het formulier de verklaring niet gebruikt: dan noemt ze niemand. Alleen de aangewezen
 * velden worden gelezen, en van elk antwoord alleen de tekst: niet wie hem schreef (`door`), niet
 * wanneer, en geen rij-id. Een veld dat alleen intern zichtbaar is (R-170) blijft leeg voor een
 * adviseur aan de externe kant.
 *
 * `zichtbaar` is de zichtbaarheid uit de regels van het formulier (`determineVisibility` in de engine).
 * Een verborgen veld houdt zijn antwoord (R-39): wie "Zijn er mede-aanvragers?" weer op Nee zet, laat de
 * rijen in de stand staan. Zonder deze toets zag de adviseur namen die de aanvrager had weggezet.
 */
export function belangPartijenOf(
  definitie: BelangSource,
  stand: Readonly<Record<string, Answer>>,
  kant: Side,
  zichtbaar: (fieldId: string) => boolean = () => true,
  dossier: Readonly<Record<string, unknown>> | null = null,
): BelangPartijen | null {
  if (!usesBelangenverklaring(definitie)) return null
  const setting = definitie.belangenverklaring ?? {}
  const byId = new Map<string, FieldFacts & { readonly rijVelden?: readonly FieldFacts[] }>()
  for (const section of definitie.secties) for (const field of section.velden) byId.set(field.id, field)
  const visible = (field: FieldFacts | undefined): boolean =>
    field !== undefined && zichtbaar(field.id) && (kant === 'intern' || field.externZichtbaar !== false)
  const read = (fieldId: string | undefined): string | null => {
    if (fieldId === undefined || !visible(byId.get(fieldId))) return null
    return textOf(stand[fieldId])
  }

  // Uit het dossier: een partij zonder formulierveld, dus zonder zichtbaarheid of kant van een veld.
  const fromDossier = (name: string): string | null => dossierText(dossier?.[name])

  let aanvrager: BelangPartij | null = null
  if (setting.aanvrager !== undefined) {
    const naam = read(setting.aanvrager.naam)
    if (naam !== null) aanvrager = { naam, organisatie: read(setting.aanvrager.organisatie) }
  } else {
    const naam = fromDossier(BELANG_DOSSIERVELDEN.aanvrager)
    if (naam !== null) aanvrager = { naam, organisatie: fromDossier(BELANG_DOSSIERVELDEN.organisatie) }
  }

  const medeAanvragers: BelangPartij[] = []
  const groupSetting = setting.medeAanvragers
  const group = groupSetting === undefined ? undefined : byId.get(groupSetting.groep)
  const answer = groupSetting === undefined ? undefined : stand[groupSetting.groep]
  if (groupSetting !== undefined && visible(group) && answer !== undefined && 'rijen' in answer) {
    const column = (id: string | undefined): FieldFacts | undefined => group?.rijVelden?.find((rowField) => rowField.id === id)
    const nameShown = visible(column(groupSetting.naam))
    const organisationShown = groupSetting.organisatie !== undefined && visible(column(groupSetting.organisatie))
    if (nameShown) {
      for (const row of answer.rijen) {
        const naam = textOf(row.waarden[groupSetting.naam] as Answer | undefined)
        if (naam === null) continue
        const organisatie =
          organisationShown && groupSetting.organisatie !== undefined ? textOf(row.waarden[groupSetting.organisatie] as Answer | undefined) : null
        medeAanvragers.push({ naam, organisatie })
      }
    }
  }

  const titel = setting.titel !== undefined ? read(setting.titel) : fromDossier(BELANG_DOSSIERVELDEN.titel)
  return { titel, aanvrager, medeAanvragers }
}
